import pageConfig from '@PROJECT_ROOT/responsiveFrontendPagesConfiguration.json';
import { apiDeleteData, apiFetchData, apiFetchDataWithoutLangISO, apiPostData, getAdriaticLang } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import windowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/windowManipulationService';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';

const lang = getAdriaticLang();

const getWishlistModalData = () => {
    const { wishlistModalDataPath } = pageConfig.wishlistPage;
    return apiFetchData(wishlistModalDataPath, '', lang);
};

const getWishlistConfirmationModalData = () => {
    const { wishlistConfirmationModalDataPath } = pageConfig.wishlistPage;
    return apiFetchData(wishlistConfirmationModalDataPath, '', lang);
};

/**
 * Dohvaća podatke za wishlist modale jednom po stranici i dijeli ih svim pozivateljima.
 * Uvijek dohvaća podatke za modal potvrde, a podatke za modal s više jedinica samo ako je `withMultiUnitModalData` true.
 * Cache je jedan, pa o tome dohvaćaju li se i podaci za više jedinica odlučuje prvi poziv:
 * kasniji pozivi dobivaju isti rezultat bez obzira na svoj argument.
 * Promise se razrješava sa spojenim podacima ili s null ako je neki zahtjev pao, nikad ne odbija.
 * Ne koristiti na SSR-u.
 * @param {boolean} [withMultiUnitModalData] treba li dohvatiti i podatke za modal s više jedinica
 */
const getWishlistModalDataOnce = (withMultiUnitModalData = false) => {
    if (!windowManipulationService.isBrowserEnvironment()) {
        throw new Error('getWishlistModalDataOnce se smije koristiti samo na klijentu.');
    }

    if (window.wishlistModalDataRequest) return window.wishlistModalDataRequest;

    window.wishlistModalDataRequest = Promise.all([
        getWishlistConfirmationModalData(),
        withMultiUnitModalData ? getWishlistModalData() : null,
    ])
        .then(([confirmationModalDataResponse, multiUnitModalDataResponse]) => ({
            ...confirmationModalDataResponse.data,
            ...multiUnitModalDataResponse?.data,
        }))
        .catch((error) => {
            reportError(error);
            return null;
        });

    return window.wishlistModalDataRequest;
};

const getWishlistInitialData = (page, queryParams, ssrLang) => {
    const { initialPathExtension, parametersForInitialRequest: { page: defaultPage, limit } } = pageConfig.wishlistPage;
    return apiFetchData(initialPathExtension, `/${page ?? defaultPage}/${limit}`, ssrLang ?? lang, queryParams);
};

const checkIsUnitWishlistedByUser = (unitId) => {
    const { isUnitWishlistedByUser } = pageConfig.facilityUnitPage;
    return apiFetchDataWithoutLangISO(`${isUnitWishlistedByUser}/${unitId}`);
};

const addWishlist = (unitId) => {
    const { addWishlistPath } = pageConfig.wishlistPage;
    return apiPostData(`${addWishlistPath}`, {
        twoLetterISOLanguageCode: lang,
        unitId,
    });
};

const removeWishlist = (unitId) => {
    const { removeWishlistPath } = pageConfig.wishlistPage;
    return apiDeleteData(`${removeWishlistPath}/${unitId}?lang=${lang}`);
};

const bulkAddWishlist = (unitIds) => {
    const { wishlistBulkAddPath } = pageConfig.searchResultsPage;
    return apiPostData(`${wishlistBulkAddPath}`, {
        twoLetterISOLanguageCode: lang,
        unitIds,
    });
};

const bulkRemoveWishlist = (unitIds) => {
    const { wishlistBulkRemovePath } = pageConfig.searchResultsPage;
    return apiPostData(`${wishlistBulkRemovePath}`, {
        twoLetterISOLanguageCode: lang,
        unitIds,
    });
};

const getWishlistLocations = (ssrLang) => {
    const { locationsPath } = pageConfig.wishlistPage;
    return apiFetchDataWithoutLangISO(`${locationsPath}/${ssrLang ?? lang}`);
};

export {
    getWishlistModalDataOnce,
    getWishlistInitialData,
    checkIsUnitWishlistedByUser,
    addWishlist,
    removeWishlist,
    bulkAddWishlist,
    bulkRemoveWishlist,
    getWishlistLocations,
};
